import React, { useMemo, useState } from 'react';
import { useAppStore } from '../../stores/useAppStore';

interface StatsModalProps {
  visible: boolean;
  onClose: () => void;
}

type TimeRange = 'day' | 'week' | 'month';

const StatsModal: React.FC<StatsModalProps> = ({ visible, onClose }) => {
  const { pomodoroLog, affinityLevel, donuts } = useAppStore();
  const [timeRange, setTimeRange] = useState<TimeRange>('week');

  // 日付処理のヘルパー関数
  const getDateKey = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // 統計データの計算
  const stats = useMemo(() => {
    const now = new Date();
    const today = getDateKey(now);
    
    // 今日の作業時間
    const todayMinutes = pomodoroLog[today] || 0;
    
    // 総作業時間
    const totalMinutes = Object.values(pomodoroLog).reduce((sum, minutes) => sum + minutes, 0);
    
    // 今週の作業時間
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - now.getDay());
    let weekMinutes = 0;
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + i);
      const key = getDateKey(date);
      weekMinutes += pomodoroLog[key] || 0;
    }
    
    // 今月の作業時間
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    let monthMinutes = 0;
    for (let date = new Date(monthStart); date <= monthEnd; date.setDate(date.getDate() + 1)) {
      const key = getDateKey(date);
      monthMinutes += pomodoroLog[key] || 0;
    }
    
    // 連続作業日数（ストリーク）の計算
    // 今日まだ作業していなくてもストリークは途切れない（昨日から遡って数える）
    let currentStreak = 0;
    const checkDate = new Date(now);
    if (!pomodoroLog[getDateKey(checkDate)]) {
      checkDate.setDate(checkDate.getDate() - 1);
    }
    while (true) {
      const key = getDateKey(checkDate);
      if (pomodoroLog[key] && pomodoroLog[key] > 0) {
        currentStreak++;
        checkDate.setDate(checkDate.getDate() - 1);
      } else {
        break;
      }
    }
    
    // 最長ストリーク
    let longestStreak = 0;
    let tempStreak = 0;
    const sortedDates = Object.keys(pomodoroLog).sort();
    let lastDate: Date | null = null;
    
    for (const dateKey of sortedDates) {
      const currentDate = new Date(dateKey);
      if (pomodoroLog[dateKey] > 0) {
        if (lastDate) {
          const dayDiff = Math.floor((currentDate.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));
          if (dayDiff === 1) {
            tempStreak++;
          } else {
            tempStreak = 1;
          }
        } else {
          tempStreak = 1;
        }
        longestStreak = Math.max(longestStreak, tempStreak);
        lastDate = currentDate;
      }
    }
    
    return {
      todayMinutes,
      totalMinutes,
      weekMinutes,
      monthMinutes,
      currentStreak,
      longestStreak,
      totalPomodoros: Math.floor(totalMinutes / 25),
      averageDaily: sortedDates.length > 0 ? Math.floor(totalMinutes / sortedDates.length) : 0,
    };
  }, [pomodoroLog]);

  // グラフ用データの準備
  const chartData = useMemo(() => {
    const data: { date: string; minutes: number; label: string }[] = [];
    const now = new Date();
    
    if (timeRange === 'day') {
      // 過去7日間
      for (let i = 6; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(now.getDate() - i);
        const key = getDateKey(date);
        const dayName = ['日', '月', '火', '水', '木', '金', '土'][date.getDay()];
        data.push({
          date: key,
          minutes: pomodoroLog[key] || 0,
          label: `${date.getMonth() + 1}/${date.getDate()}(${dayName})`,
        });
      }
    } else if (timeRange === 'week') {
      // 過去4週間
      for (let i = 3; i >= 0; i--) {
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - now.getDay() - i * 7);
        let weekMinutes = 0;
        for (let j = 0; j < 7; j++) {
          const date = new Date(weekStart);
          date.setDate(weekStart.getDate() + j);
          const key = getDateKey(date);
          weekMinutes += pomodoroLog[key] || 0;
        }
        data.push({
          date: getDateKey(weekStart),
          minutes: weekMinutes,
          label: i === 0 ? '今週' : `${i}週間前`,
        });
      }
    } else {
      // 過去12ヶ月
      for (let i = 11; i >= 0; i--) {
        // 月末日にsetMonthすると存在しない日付で翌月にオーバーフローするため、1日固定で生成
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthStart = date;
        const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        let monthMinutes = 0;
        for (let d = new Date(monthStart); d <= monthEnd; d.setDate(d.getDate() + 1)) {
          const key = getDateKey(d);
          monthMinutes += pomodoroLog[key] || 0;
        }
        data.push({
          date: getDateKey(monthStart),
          minutes: monthMinutes,
          label: `${date.getMonth() + 1}月`,
        });
      }
    }
    
    return data;
  }, [pomodoroLog, timeRange]);

  // 最大値を計算（グラフのスケール用）
  const maxMinutes = Math.max(...chartData.map(d => d.minutes), 1);

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 glass-overlay z-50 flex items-center justify-center p-4 fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="glass-panel p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
           style={{ borderRadius: '20px' }}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white">
            Statistics Dashboard
          </h2>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white transition-colors text-2xl"
          >
            ×
          </button>
        </div>

        {/* 褒めメッセージ */}
        <div className="glass-inner p-4 rounded-xl mb-6">
          <div className="text-center">
            {stats.todayMinutes === 0 ? (
              <p className="text-lg font-medium text-white/90">
                今日もアプリを開いてくれた！それだけで目標達成だよ！ ✨
              </p>
            ) : stats.todayMinutes < 25 ? (
              <p className="text-lg font-medium text-white/90">
                今日は{formatTime(stats.todayMinutes)}も作業した！素晴らしい！ 🌟
              </p>
            ) : (
              <p className="text-lg font-medium text-white/90">
                今日は{formatTime(stats.todayMinutes)}も頑張った！完璧だよ！ 🎉
              </p>
            )}
            {stats.currentStreak > 1 && (
              <p className="text-sm text-white/50 mt-1">
                🔥 {stats.currentStreak}日連続！継続できてるのが本当にすごい！
              </p>
            )}
          </div>
        </div>

        {/* サマリーカード */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="glass-inner p-4 rounded-xl">
            <div className="text-xs text-white/40 mb-1 uppercase tracking-wider font-semibold">Today</div>
            <div className="text-2xl font-bold text-white">{formatTime(stats.todayMinutes)}</div>
          </div>
          <div className="glass-inner p-4 rounded-xl">
            <div className="text-xs text-white/40 mb-1 uppercase tracking-wider font-semibold">Total</div>
            <div className="text-2xl font-bold text-white">{formatTime(stats.totalMinutes)}</div>
          </div>
          <div className="glass-inner p-4 rounded-xl">
            <div className="text-xs text-white/40 mb-1 uppercase tracking-wider font-semibold">Streak</div>
            <div className="text-2xl font-bold text-white">{stats.currentStreak} days</div>
          </div>
          <div className="glass-inner p-4 rounded-xl">
            <div className="text-xs text-white/40 mb-1 uppercase tracking-wider font-semibold">Level</div>
            <div className="text-2xl font-bold text-white">Lv.{affinityLevel}</div>
          </div>
        </div>

        {/* 期間選択タブ */}
        <div className="flex gap-2 mb-4">
          {(['day', 'week', 'month'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-4 py-2 rounded-lg transition-all ${
                timeRange === range
                  ? 'bg-blue-500/30 text-white border border-blue-500/30'
                  : 'bg-white/[0.06] text-white/50 border border-white/[0.06] hover:bg-white/[0.1] hover:text-white'
              }`}
            >
              {range === 'day' ? 'Daily' : range === 'week' ? 'Weekly' : 'Monthly'}
            </button>
          ))}
        </div>

        {/* グラフ */}
        <div className="glass-inner p-6 rounded-xl mb-6">
          <h3 className="text-sm font-semibold mb-4 text-white/50 uppercase tracking-wider">Activity Graph</h3>
          <div className="space-y-3">
            {chartData.map((data, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="text-xs text-white/40 w-20 text-right font-medium">{data.label}</div>
                <div className="flex-1 h-8 bg-white/[0.04] rounded-full relative overflow-hidden">
                  <div
                    className="absolute top-0 left-0 h-full bg-blue-500/50 rounded-full transition-all duration-700 ease-out"
                    style={{ width: `${(data.minutes / maxMinutes) * 100}%` }}
                  />
                  {data.minutes > 0 && (
                    <span className="absolute inset-0 flex items-center px-3 text-xs font-bold text-white drop-shadow-lg">
                      {formatTime(data.minutes)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 詳細統計 */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="glass-inner p-4 rounded-xl">
            <h3 className="text-sm font-semibold text-white/50 mb-3 uppercase tracking-wider">Statistics</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-white/40">Total Pomodoros</span>
                <span className="font-bold text-white">{stats.totalPomodoros}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/40">Daily Average</span>
                <span className="font-bold text-white">{formatTime(stats.averageDaily)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/40">Longest Streak</span>
                <span className="font-bold text-white">{stats.longestStreak} days</span>
              </div>
            </div>
          </div>
          <div className="glass-inner p-4 rounded-xl">
            <h3 className="text-sm font-semibold text-white/50 mb-3 uppercase tracking-wider">Progress</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-white/40">Donuts</span>
                <span className="font-bold text-white flex items-center gap-1">
                  🍩 {donuts}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/40">This Week</span>
                <span className="font-bold text-white">{formatTime(stats.weekMinutes)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/40">This Month</span>
                <span className="font-bold text-white">{formatTime(stats.monthMinutes)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* 閉じるボタン */}
        <button
          onClick={onClose}
          className="w-full py-3 px-4 glass-btn transition-all font-medium"
          style={{ borderRadius: '12px' }}
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default StatsModal;